import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, MagnifyingGlass, X, WarningCircle } from '@phosphor-icons/react';
import VenueMap from './VenueMap';
import {
  resolveGoogleLocation,
  reverseGoogleLocation,
  searchGoogleLocations,
} from '../lib/googleMaps';
import { hasVenueCoordinates } from '../lib/locationLinks';

const venueDetails = (value, overrides = {}) => ({
  name: value?.name || '',
  address: value?.address || '',
  lat: value?.lat ?? null,
  lng: value?.lng ?? null,
  hall_name: value?.hall_name || '',
  floor: value?.floor || '',
  notes: value?.notes || '',
  ...overrides,
});

const normalizeSavedVenuePart = (value) => (
  String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
);

const getSavedVenueKey = (venue) => {
  const name = normalizeSavedVenuePart(venue?.name);
  const address = normalizeSavedVenuePart(venue?.address);
  return name || address;
};

export default function VenueSelector({
  value,
  onChange,
  showMap = true,
  savedVenues = [],
}) {
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [searchInput, setSearchInput] = useState(value?.address || value?.name || '');

  const reverseControllerRef = useRef(null);
  const reverseRequestIdRef = useRef(0);
  const searchRequestIdRef = useRef(0);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!showPredictions) {
      setSearchInput(value?.address || value?.name || '');
    }
  }, [showPredictions, value?.address, value?.name]);

  const reusableVenues = useMemo(() => {
    const seen = new Set();
    return (savedVenues || []).filter((venue) => {
      const key = getSavedVenueKey(venue);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }, [savedVenues]);

  const applyVenue = useCallback((venue) => {
    const nextVenue = venueDetails(valueRef.current, venue);
    valueRef.current = nextVenue;
    onChangeRef.current(nextVenue);
    setSearchInput(nextVenue.address || nextVenue.name || '');
    setPredictions([]);
    setShowPredictions(false);
    setSearchError('');
  }, []);

  const selectCoordinates = useCallback(async (lat, lng) => {
    const baseValue = valueRef.current || {};
    applyVenue({
      name: baseValue.name || 'Selected location',
      address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      lat,
      lng,
    });

    const requestId = ++reverseRequestIdRef.current;
    reverseControllerRef.current?.abort();
    setIsReverseGeocoding(true);
    try {
      const result = await reverseGoogleLocation(lat, lng);
      if (result && requestId === reverseRequestIdRef.current) {
        applyVenue({
          name: baseValue.name && baseValue.name !== 'Selected location'
            ? baseValue.name
            : result.name,
          address: result.address,
          lat,
          lng,
        });
      }
    } catch (error) {
      if (error.code !== 'ERR_CANCELED' && requestId === reverseRequestIdRef.current) {
        setSearchError(error.response?.data?.detail || 'Address lookup failed. You can enter it manually.');
      }
    } finally {
      if (requestId === reverseRequestIdRef.current) {
        reverseControllerRef.current = null;
        setIsReverseGeocoding(false);
      }
    }
  }, [applyVenue]);

  useEffect(() => () => reverseControllerRef.current?.abort(), []);

  useEffect(() => {
    const query = searchInput.trim();
    if (
      query.length < 3
      || query === value?.address
    ) {
      setPredictions([]);
      setShowPredictions(false);
      setIsSearching(false);
      return undefined;
    }

    const requestId = ++searchRequestIdRef.current;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const nextPredictions = await searchGoogleLocations(query);
        if (requestId !== searchRequestIdRef.current) return;
        setPredictions(nextPredictions);
        setShowPredictions(nextPredictions.length > 0);
        setSearchError('');
      } catch (error) {
        if (error.code !== 'ERR_CANCELED' && requestId === searchRequestIdRef.current) {
          setPredictions([]);
          setShowPredictions(false);
          setSearchError(error.response?.data?.detail || 'Venue search is unavailable. Enter the venue manually.');
        }
      } finally {
        if (requestId === searchRequestIdRef.current) setIsSearching(false);
      }
    }, 350);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput, value?.address]);

  const handlePredictionSelect = async (prediction) => {
    setIsSearching(true);
    try {
      const result = await resolveGoogleLocation(prediction);
      if (result) applyVenue(result);
    } catch {
      setSearchError('Venue details could not be loaded. Try another result or enter them manually.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleMapError = useCallback((message) => {
    setSearchError(message);
  }, []);

  const handleManualChange = (field, fieldValue) => {
    const nextVenue = venueDetails(valueRef.current, { [field]: fieldValue });
    valueRef.current = nextVenue;
    onChangeRef.current(nextVenue);
  };

  const handleClear = () => {
    setSearchInput('');
    setPredictions([]);
    setShowPredictions(false);
    setSearchError('');
    const emptyVenue = venueDetails(null);
    valueRef.current = emptyVenue;
    onChangeRef.current(emptyVenue);
  };

  return (
    <div className="space-y-4">
      {reusableVenues.length > 0 && (
        <div data-testid="saved-venues">
          <Label>Existing venues</Label>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose a venue already used in this itinerary.
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {reusableVenues.map((venue) => {
              const key = getSavedVenueKey(venue);
              const selected = key === getSavedVenueKey(value);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyVenue(venue)}
                  className={`flex min-w-0 items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors ${
                    selected
                      ? 'border-wedding-sage bg-wedding-sage/10'
                      : 'border-border hover:border-wedding-sage/60 hover:bg-wedding-sage/5'
                  }`}
                  aria-pressed={selected}
                  data-testid={`saved-venue-${key}`}
                >
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-wedding-sage" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {venue.name || venue.address}
                    </span>
                    {venue.name && venue.address && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {venue.address}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="relative">
        <Label htmlFor="venue-search">Search venue or address</Label>
        <div className="relative mt-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="venue-search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onFocus={() => predictions.length > 0 && setShowPredictions(true)}
            placeholder="Search for venue, hotel, banquet hall..."
            className="pl-10 pr-10"
            autoComplete="off"
            data-testid="venue-search-input"
          />
          {(searchInput || isSearching) && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear venue"
            >
              {isSearching ? <span className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <X className="h-4 w-4" />}
            </button>
          )}
        </div>

        {showPredictions && predictions.length > 0 && (
          <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {predictions.map((prediction, index) => (
              <button
                key={prediction.place_id || `${prediction.lat}-${prediction.lng}-${index}`}
                type="button"
                onClick={() => handlePredictionSelect(prediction)}
                className="flex w-full items-start gap-3 border-b px-4 py-3 text-left hover:bg-wedding-sage/10 last:border-b-0"
                data-testid={`venue-prediction-${prediction.place_id || index}`}
              >
                <MapPin className="mt-1 h-4 w-4 flex-shrink-0 text-wedding-sage" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{prediction.name}</p>
                  <p className="text-xs text-muted-foreground">{prediction.address}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {searchError && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
          <WarningCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{searchError}</span>
        </div>
      )}

      {showMap && (
        <VenueMap
          value={value}
          fallbackVenue={reusableVenues.find(hasVenueCoordinates)}
          onSelect={selectCoordinates}
          onError={handleMapError}
          busy={isReverseGeocoding}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="venue_name">Venue Name *</Label>
          <Input
            id="venue_name"
            value={value?.name || ''}
            onChange={(event) => handleManualChange('name', event.target.value)}
            placeholder="e.g., Grand Hyatt"
            data-testid="venue-name-input"
          />
        </div>
        <div>
          <Label htmlFor="hall_name">Hall/Garden Name</Label>
          <Input
            id="hall_name"
            value={value?.hall_name || ''}
            onChange={(event) => handleManualChange('hall_name', event.target.value)}
            placeholder="e.g., Grand Ballroom"
            data-testid="venue-hall-input"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="venue_address">Address *</Label>
        <Input
          id="venue_address"
          value={value?.address || ''}
          onChange={(event) => handleManualChange('address', event.target.value)}
          placeholder="Full address of the venue"
          data-testid="venue-address-input"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="floor">Floor</Label>
          <Input
            id="floor"
            value={value?.floor || ''}
            onChange={(event) => handleManualChange('floor', event.target.value)}
            placeholder="e.g., Ground Floor"
            data-testid="venue-floor-input"
          />
        </div>
        <div>
          <Label htmlFor="notes">Entry Notes</Label>
          <Input
            id="notes"
            value={value?.notes || ''}
            onChange={(event) => handleManualChange('notes', event.target.value)}
            placeholder="e.g., Enter through Gate 2"
            data-testid="venue-notes-input"
          />
        </div>
      </div>
    </div>
  );
}
